import type { ReactNode } from 'react';
import {
  Controller,
  type Control,
  type FieldErrors,
  type UseFormRegister,
  type UseFormSetValue,
  type UseFormWatch,
} from 'react-hook-form';
import { SectionCard } from '@/components/common/SectionCard';
import { SectorFields } from '@/features/startups/components/SectorFields';
import { HqLocationFields } from '@/components/common/HqLocationFields';
import { TextField } from '@/components/forms/TextField';
import { TextareaField } from '@/components/forms/TextareaField';
import { SelectField } from '@/components/forms/SelectField';
import { FieldLabel } from '@/components/forms/FieldLabel';
import { UI_STRINGS } from '@/lib/constants/ui-strings';
import { joinTruthy, withDetail } from '@/lib/utils/format.utils';
import { FIELD_LABELS, FUNDING_STAGE_OPTIONS } from '@/features/startups/constants';
import type { StartupProfileData } from '../../api/startup-profile.api';
import type { StartupProfileFormValues } from './schema';
import { NUMERIC_INPUT_MAX_DIGITS_COUNT, NUMERIC_INPUT_MAX_DIGITS_YEAR } from '@/lib/constants/numeric-input';

const S = UI_STRINGS.STARTUP_PROFILE;

function Fact({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="flex flex-col gap-1">
      <dt>
        <FieldLabel label={label} variant="caps" />
      </dt>
      <dd className="text-navy text-p6">
        {value || <span className="text-slate">{S.NOT_ADDED}</span>}
      </dd>
    </div>
  );
}

interface SectionProps {
  /** View mode only: extra header content, e.g. the create wizard review's Edit link. */
  headerRight?: ReactNode;
  mode: 'view' | 'edit';
  data: StartupProfileData;
  control?: Control<StartupProfileFormValues>;
  register?: UseFormRegister<StartupProfileFormValues>;
  setValue?: UseFormSetValue<StartupProfileFormValues>;
  watch?: UseFormWatch<StartupProfileFormValues>;
  errors?: FieldErrors<StartupProfileFormValues>;
}

/** Company Overview card: identity, sector/stage, HQ location, milestone, links. */
export function CompanyOverviewSection({
  mode,
  data,
  control,
  register,
  setValue,
  watch,
  errors,
  headerRight,
}: SectionProps) {
  if (mode === 'view' || !control || !register || !setValue || !watch) {
    const primarySector = data.sectors?.find((row) => row.isPrimary)?.sector;
    const subsectors = data.sectors
      ?.filter((row) => !row.isPrimary)
      .map((row) => row.subsector)
      .filter(Boolean)
      .join(', ');
    const location = joinTruthy([data.hqCity, data.hqState, data.hqCountry]);

    return (
      <SectionCard title={S.COMPANY_OVERVIEW} headerRight={headerRight}>
        <dl className="flex flex-col gap-4">
          <Fact label={S.COMPANY_NAME} value={data.companyName} />
          <Fact label={S.BRIEF_DESCRIPTION} value={data.briefDescription} />
          <Fact label={S.SECTOR} value={withDetail(primarySector, data.sectorOther)} />
          {subsectors && <Fact label={S.SUBSECTORS} value={subsectors} />}
          <Fact
            label={S.FUNDING_STAGE}
            value={data.fundingStage ? humanizeFundingStage(data.fundingStage) : undefined}
          />
          <Fact label={S.YEAR_FOUNDED} value={data.yearFounded?.toString()} />
          <Fact label={FIELD_LABELS.employeeCount} value={data.employeeCount?.toString()} />
          <Fact label={S.HQ_LOCATION} value={location} />
          <Fact label={S.HQ_REGION} value={data.hqWorldRegion} />
          <Fact label={S.RECENT_MILESTONE} value={data.recentMilestone} />
          <Fact label={S.COMPANY_WEBSITE} value={data.companyWebsiteUrl} />
          <Fact label={S.COMPANY_LINKEDIN} value={data.companyLinkedinUrl} />
        </dl>
      </SectionCard>
    );
  }

  return (
    <CompanyOverviewEditor
      control={control}
      register={register}
      setValue={setValue}
      watch={watch}
      errors={errors}
    />
  );
}

function humanizeFundingStage(value: string): string {
  return FUNDING_STAGE_OPTIONS.find((option) => option.value === value)?.label ?? value;
}

function CompanyOverviewEditor({
  control,
  register,
  setValue,
  watch,
  errors,
}: {
  control: Control<StartupProfileFormValues>;
  register: UseFormRegister<StartupProfileFormValues>;
  setValue: UseFormSetValue<StartupProfileFormValues>;
  watch: UseFormWatch<StartupProfileFormValues>;
  errors?: FieldErrors<StartupProfileFormValues>;
}) {
  const setId = (
    name: 'sectorId' | 'hqCountryId' | 'hqStateId' | 'hqCityId',
    value: number | undefined,
  ) => setValue(name, value, { shouldValidate: true, shouldDirty: true });

  // A saved non-US state is left untouched (no longer cleared on open, which
  // lost data): the Backend derives it from the city. Picking a new non-US
  // city drops it (HqLocationFields), and toApiPayload then omits it.
  return (
    <SectionCard title={S.COMPANY_OVERVIEW}>
      <div className="flex flex-col gap-5">
        <TextField
          {...register('companyName')}
          label={S.COMPANY_NAME}
          labelVariant="caps"
          required
          error={errors?.companyName?.message}
        />
        <TextareaField
          {...register('briefDescription')}
          label={S.BRIEF_DESCRIPTION}
          labelVariant="caps"
          rows={4}
          error={errors?.briefDescription?.message}
        />
        <SectorFields
          value={{
            sectorId: watch('sectorId'),
            subsectorIds: watch('subsectorIds') ?? [],
            sectorOther: watch('sectorOther'),
          }}
          onChange={(next) => {
            setId('sectorId', next.sectorId);
            setValue('subsectorIds', next.subsectorIds, {
              shouldValidate: true,
              shouldDirty: true,
            });
            setValue('sectorOther', next.sectorOther, { shouldValidate: true, shouldDirty: true });
          }}
          sectorLabel={S.SECTOR}
          subsectorsLabel={S.SUBSECTORS}
          error={errors?.sectorId?.message}
          sectorOtherError={errors?.sectorOther?.message}
        />
        {/* Short values share a row: a stage, a year, a head count. */}
        <div className="grid gap-5 sm:grid-cols-3">
          <SelectField
            {...register('fundingStage')}
            label={S.FUNDING_STAGE}
            labelVariant="caps"
            options={FUNDING_STAGE_OPTIONS}
            error={errors?.fundingStage?.message}
          />
          <Controller
            control={control}
            name="yearFounded"
            render={({ field }) => (
              <TextField
                {...field}
                numeric
                groupThousands={false}
                maxDigits={NUMERIC_INPUT_MAX_DIGITS_YEAR}
                label={S.YEAR_FOUNDED}
                labelVariant="caps"
                error={errors?.yearFounded?.message}
              />
            )}
          />
          <Controller
            control={control}
            name="employeeCount"
            render={({ field }) => (
              <TextField
                {...field}
                numeric
                maxDigits={NUMERIC_INPUT_MAX_DIGITS_COUNT}
                label={FIELD_LABELS.employeeCount}
                labelVariant="caps"
                error={errors?.employeeCount?.message}
              />
            )}
          />
        </div>
        <HqLocationFields
          value={{
            countryId: watch('hqCountryId'),
            stateId: watch('hqStateId'),
            cityId: watch('hqCityId'),
            cityName: watch('hqCityName'),
          }}
          onChange={(next) => {
            setId('hqCountryId', next.countryId);
            setId('hqStateId', next.stateId);
            setId('hqCityId', next.cityId);
            setValue('hqCityName', next.cityName, { shouldDirty: true });
          }}
          countryLabel={FIELD_LABELS.hqCountry}
          stateLabel={FIELD_LABELS.hqState}
          cityLabel={FIELD_LABELS.hqCity}
          errors={{
            countryId: errors?.hqCountryId?.message,
            stateId: errors?.hqStateId?.message,
            cityId: errors?.hqCityId?.message,
          }}
        />
        <TextareaField
          {...register('recentMilestone')}
          label={S.RECENT_MILESTONE}
          labelVariant="caps"
          rows={3}
          error={errors?.recentMilestone?.message}
        />
        {/* The two links share a row. */}
        <div className="grid gap-5 sm:grid-cols-2">
          <TextField
            {...register('companyWebsiteUrl')}
            label={S.COMPANY_WEBSITE}
            labelVariant="caps"
            type="url"
            error={errors?.companyWebsiteUrl?.message}
          />
          <TextField
            {...register('companyLinkedinUrl')}
            label={S.COMPANY_LINKEDIN}
            labelVariant="caps"
            type="url"
            error={errors?.companyLinkedinUrl?.message}
          />
        </div>
      </div>
    </SectionCard>
  );
}
